/**
 * CustomerInformationCard
 *
 * The single Sales-deal customer card, reused by the Showroom details slideout, the financing flow,
 * and deal setup. Always renders the store-backed `UnifiedCustomerCard` (buyer, plus a co-buyer tab
 * when the deal has one) opened on the requested tab. Store wiring (save/reconcile + deal id) comes
 * from `useSCICustomerCard`.
 *
 * The activity store is seeded by the SCI customer-card middleware (not by this component), so every
 * mode just renders the store-backed card.
 *
 * Modes are prop-driven:
 *  - Showroom slideout: pass `onClose` (shows Cancel + closes on save) plus `syncSelectedCustomer`.
 *  - Embedded (financing): omit `onClose` (Apply-only footer); the host frames it with its own
 *    title/subtitle.
 *  - Controlled (deal setup): forward a `ref` and pass `onSave` + `onStateChange` + `hideFooter` so an
 *    external action bar (the Launch button) drives `apply()` and reads canApply/isSaving.
 *
 * Convert-to-Business is wired once here, host-agnostic: the entry CTA is passed into the activity's
 * `personalInfoHeaderAction` slot (renders beside the buyer's "Personal Information" heading), and the
 * footer swaps to Continue/Cancel while the business-search view is active — the same behaviour in
 * every host, gated purely by the toggle/capability/phase from `useSCICustomerCard`.
 */
import { forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';

import { UnifiedCustomerCard } from '@makemydeal/dr-activities-customer-card';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { Button } from '@makemydeal/dr-ui-bricks-components';
import { ArrowsRightLeftIcon } from '@interstate/components/Icons/ArrowsRightLeftIcon';
import { XMarkIcon } from '@interstate/components/Icons/XMarkIcon';

import { CustomerInformationFooter } from './CustomerInformationFooter';
import {
    StyledCardHeader,
    StyledCtaLabel,
    StyledHeaderActions,
    StyledShowroomCardScroll,
    StyledSlideoutClose
} from './CustomerInformationExperience.style';
import { PageHeader } from '../PageHeader/PageHeader';
import { useCustomerCardCdlAnalytics, useSCICustomerCard } from '../../hooks';
import type { CustomerInformationCardProps, CustomerInformationCardRef } from '../../types';
import { HOST_APP } from '../../constants';

export const CustomerInformationCard = forwardRef<CustomerInformationCardRef, CustomerInformationCardProps>(
    (
        {
            heading,
            customer,
            initialTab: initialTabOverride,
            onClose,
            syncSelectedCustomer = false,
            hideLoader = false,
            saveLabel,
            onSave,
            onStateChange,
            hideFooter = false,
            showSaveSuccessBanner = false,
            showSaveLoader = false
        },
        ref
    ) => {
        const {
            dealId,
            initialTab,
            enableSpd,
            enableBusinessConversion,
            cardRef,
            onSave: handleSave,
            onCardStateChange,
            onApply,
            onCancel,
            onAnalyticsEvent,
            onConvertToBusiness,
            onContinueBusinessConversion,
            onCancelBusinessConversion,
            onBusinessConversionComplete,
            isSaving,
            canApply,
            canCancel,
            canContinue,
            showConvertToBusinessCta,
            isBusinessSearchStep
        } = useSCICustomerCard({
            initialTab: initialTabOverride ?? customer,
            onClose,
            syncSelectedCustomer,
            onSave,
            onStateChange
        });

        // UnifiedCustomerCard takes one onAnalyticsEvent prop, but two independent concerns feed
        // it: useSCICustomerCard's own handler (CREDIT_CONSENT_CLICKED) and this host's CDL mapping
        // (dc-sci-089–093) — both must run, so they're composed here rather than passed separately.
        const handleCustomerCardAnalyticsEvent = useCustomerCardCdlAnalytics(onAnalyticsEvent);
        const isProcessSciDtUpdatesEnabled = useSelector(featureToggleSelectors.isProcessSciDtUpdatesEnabled);
        const hideCheckLoader = useSelector(featureToggleSelectors.isSciCustomerCardPreloaderEnabled);

        // Expose the card's imperative handle to a controlling host (deal setup's Launch button).
        useImperativeHandle(
            ref,
            () => ({
                apply: () => cardRef.current?.apply() ?? Promise.resolve(),
                cancel: () => cardRef.current?.cancel()
            }),
            []
        );

        return (
            <>
                {(heading || onClose) && (
                    <StyledCardHeader>
                        {heading && <PageHeader isResponsive heading={heading} />}
                        <StyledHeaderActions>
                            {onClose && (
                                <StyledSlideoutClose
                                    type="button"
                                    aria-label="Close"
                                    data-testid="customer-card-close-button"
                                    onClick={isBusinessSearchStep ? onCancelBusinessConversion : onCancel}
                                >
                                    <XMarkIcon aria-hidden />
                                </StyledSlideoutClose>
                            )}
                        </StyledHeaderActions>
                    </StyledCardHeader>
                )}
                <StyledShowroomCardScroll data-testid="customer-card-scroll-region">
                    <UnifiedCustomerCard
                        ref={cardRef}
                        dealId={dealId}
                        initialTab={initialTab}
                        capabilities={{
                            enableSpd,
                            enableBusinessConversion,
                            enableCreditAppFields: true,
                            enableSpdReveal: false,
                            enableChangeBuyerCoBuyer: false,
                            // SCI opts in directly rather than waiting on F311457_Enable_Credit_Consent; MV
                            // omits this and follows the toggle.
                            enableCreditConsent: true,
                            enableCreditConsentSentMessageSuppression: isProcessSciDtUpdatesEnabled
                        }}
                        onSave={handleSave}
                        onCancel={onClose}
                        onStateChange={onCardStateChange}
                        onBusinessConversionComplete={onBusinessConversionComplete}
                        onAnalyticsEvent={handleCustomerCardAnalyticsEvent}
                        hostApp={HOST_APP}
                        hideLoader={hideLoader}
                        hideCheckLoader={hideCheckLoader}
                        showSaveSuccessBanner={showSaveSuccessBanner}
                        personalInfoHeaderAction={
                            showConvertToBusinessCta ? (
                                <Button
                                    data-testid="customer-card-convert-to-business-button"
                                    buttonStyle="tertiary"
                                    buttonSize="small"
                                    width="auto"
                                    onClick={onConvertToBusiness}
                                >
                                    <StyledCtaLabel>
                                        <ArrowsRightLeftIcon aria-hidden />
                                        Convert to Business
                                    </StyledCtaLabel>
                                </Button>
                            ) : undefined
                        }
                    />
                </StyledShowroomCardScroll>
                {isBusinessSearchStep ? (
                    // The business search's Continue/Cancel live in the footer, so it renders even when
                    // `hideFooter` hides the default Apply/Cancel bar (deal setup drives its own Launch bar).
                    <CustomerInformationFooter
                        variant="search"
                        onCancel={onCancelBusinessConversion}
                        onSave={onContinueBusinessConversion}
                        disabled={!canContinue}
                    />
                ) : (
                    !hideFooter && (
                        <CustomerInformationFooter
                            onCancel={onClose ? onCancel : undefined}
                            onSave={onApply}
                            disabled={isSaving || !canApply}
                            cancelDisabled={isSaving || !canCancel}
                            saving={showSaveLoader && isSaving}
                            saveLabel={saveLabel}
                        />
                    )
                )}
            </>
        );
    }
);

CustomerInformationCard.displayName = 'CustomerInformationCard';

export default CustomerInformationCard;
